package fr.frux.core.designsystem.theme

import androidx.compose.material3.Typography
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.sp
import fr.frux.core.designsystem.resources.Res
import fr.frux.core.designsystem.resources.figtree_medium
import fr.frux.core.designsystem.resources.figtree_regular
import fr.frux.core.designsystem.resources.figtree_semibold
import fr.frux.core.designsystem.resources.sora_bold
import fr.frux.core.designsystem.resources.sora_semibold
import org.jetbrains.compose.resources.Font

// Sora (OFL, google/fonts) remplace Fraunces en display/titres (décision Fred 2026-08-09,
// duo Sora display + Figtree body de la proposition frux-design). Instances statiques dérivées
// de la variable font via fonttools (varLib.instancer, wght=600/700).
@Composable
internal fun soraFamily() =
    FontFamily(
        Font(Res.font.sora_semibold, FontWeight.SemiBold),
        Font(Res.font.sora_bold, FontWeight.Bold),
    )

// Figtree (OFL) remplace Inter en body (#198, piste médiane) : un grotesque plus chaud,
// moins "corporate" qu'Inter, tout en gardant Sora en display/titres pour l'identité de marque.
@Composable
internal fun figtreeFamily() =
    FontFamily(
        Font(Res.font.figtree_regular, FontWeight.Normal),
        Font(Res.font.figtree_medium, FontWeight.Medium),
        Font(Res.font.figtree_semibold, FontWeight.SemiBold),
    )

/**
 * Échelle recalée pour le duo Sora/Figtree (l'échelle héritée gardait les métriques M3 pensées
 * pour Roboto) :
 * - Sora est une géométrique large et aérée : tout l'étage display/headline/titre reçoit un
 *   tracking négatif progressif (fort en grand corps, dégressif en descendant), sinon les titres
 *   flottent — seul displayLarge en avait.
 * - Figtree est plus ronde et plus généreuse que Roboto : les letter-spacings M3 positifs
 *   (0.5sp en bodyLarge !) la desserrent trop ; on les ramène près de zéro, en gardant un peu
 *   d'air sur les petits corps (bodySmall, labels) où il aide la lisibilité.
 */
@Composable
internal fun fruxTypography(): Typography {
    val sora = soraFamily()
    val figtree = figtreeFamily()
    // Construction pure (aucun appel composable) : remember() l'évite à chaque recomposition de
    // FruxTheme (#91), sora/figtree ne changeant pour leur part jamais d'une frame à l'autre.
    return remember(sora, figtree) { buildFruxTypography(sora, figtree) }
}

// Table plate d'un champ Typography par style : le reformatage ktlint (un .copy() par ligne)
// pousse la fonction au-delà du seuil LongMethod sans que la complexité réelle change — scinder
// la table en morceaux nuirait à la lisibilité de la correspondance style → valeurs.
@Suppress("LongMethod")
private fun buildFruxTypography(
    sora: FontFamily,
    figtree: FontFamily,
): Typography {
    val base = Typography()
    return Typography(
        displayLarge =
            base.displayLarge.copy(
                fontFamily = sora,
                fontWeight = FontWeight.Bold,
                fontSize = 36.sp,
                lineHeight = 42.sp,
                letterSpacing = (-0.7).sp,
            ),
        displayMedium =
            base.displayMedium.copy(
                fontFamily = sora,
                fontWeight = FontWeight.Bold,
                fontSize = 30.sp,
                lineHeight = 36.sp,
                letterSpacing = (-0.5).sp,
            ),
        displaySmall =
            base.displaySmall.copy(
                fontFamily = sora,
                fontWeight = FontWeight.Bold,
                fontSize = 26.sp,
                lineHeight = 32.sp,
                letterSpacing = (-0.4).sp,
            ),
        headlineLarge =
            base.headlineLarge.copy(
                fontFamily = sora,
                fontWeight = FontWeight.Bold,
                fontSize = 24.sp,
                lineHeight = 30.sp,
                letterSpacing = (-0.35).sp,
            ),
        headlineMedium =
            base.headlineMedium.copy(
                fontFamily = sora,
                fontWeight = FontWeight.SemiBold,
                fontSize = 21.sp,
                lineHeight = 28.sp,
                letterSpacing = (-0.25).sp,
            ),
        headlineSmall =
            base.headlineSmall.copy(
                fontFamily = sora,
                fontWeight = FontWeight.SemiBold,
                fontSize = 19.sp,
                lineHeight = 26.sp,
                letterSpacing = (-0.2).sp,
            ),
        titleLarge =
            base.titleLarge.copy(
                fontFamily = sora,
                fontWeight = FontWeight.SemiBold,
                fontSize = 18.sp,
                lineHeight = 24.sp,
                letterSpacing = (-0.15).sp,
            ),
        titleMedium =
            base.titleMedium.copy(
                fontFamily = figtree,
                fontWeight = FontWeight.SemiBold,
                fontSize = 16.sp,
                lineHeight = 22.sp,
                letterSpacing = 0.sp,
            ),
        titleSmall =
            base.titleSmall.copy(
                fontFamily = figtree,
                fontWeight = FontWeight.Medium,
                fontSize = 14.sp,
                lineHeight = 20.sp,
                letterSpacing = 0.sp,
            ),
        bodyLarge =
            base.bodyLarge.copy(
                fontFamily = figtree,
                fontSize = 16.sp,
                lineHeight = 24.sp,
                letterSpacing = 0.1.sp,
            ),
        bodyMedium =
            base.bodyMedium.copy(
                fontFamily = figtree,
                fontSize = 14.sp,
                lineHeight = 20.sp,
                letterSpacing = 0.1.sp,
            ),
        bodySmall =
            base.bodySmall.copy(
                fontFamily = figtree,
                fontSize = 12.sp,
                lineHeight = 16.sp,
                letterSpacing = 0.2.sp,
            ),
        labelLarge =
            base.labelLarge.copy(
                fontFamily = figtree,
                fontWeight = FontWeight.SemiBold,
                fontSize = 14.sp,
                lineHeight = 20.sp,
                letterSpacing = 0.1.sp,
            ),
        labelMedium =
            base.labelMedium.copy(
                fontFamily = figtree,
                fontWeight = FontWeight.SemiBold,
                fontSize = 12.sp,
                lineHeight = 16.sp,
                letterSpacing = 0.3.sp,
            ),
        labelSmall =
            base.labelSmall.copy(
                fontFamily = figtree,
                fontWeight = FontWeight.SemiBold,
                fontSize = 11.sp,
                lineHeight = 14.sp,
                letterSpacing = 0.8.sp,
            ),
    )
}
